/**
 * Onboarding Template Service
 * 
 * Applies industry-specific templates to organizations
 * Creates products, configures stages, generates sample data
 */

import { collection, addDoc, doc, updateDoc, Timestamp, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getTemplateById } from '../data/systemOnboardingTemplates';
import { Product } from '../types';

// ============================================================================
// Template Application
// ============================================================================

/**
 * Apply template to organization
 * Creates products, sets up stages, optionally generates sample data
 */
export async function applyTemplateToOrganization(
  orgId: string,
  templateId: string,
  options: {
    includeSampleData?: boolean;
    userId: string;
  }
): Promise<{
  productsCreated: number;
  sampleDataCreated: number;
}> {
  try {
    const template = getTemplateById(templateId);
    if (!template) {
      throw new Error(`Template ${templateId} not found`);
    }
    
    console.log(`📋 Applying template "${template.name}" to organization ${orgId}`);
    
    // STEP 1: Create products
    console.log(`  Creating ${template.defaultProducts.length} products...`);
    const productsCreated = await createProductsFromTemplate(
      orgId,
      template.defaultProducts,
      template.leadStages,
      template.opportunityStages
    );
    
    // STEP 2: Update organization with template metadata
    await updateDoc(doc(db, 'organizations', orgId), {
      onboardingData: {
        appliedTemplate: templateId,
        templateName: template.name,
        industry: template.industry,
        appliedAt: Timestamp.now()
      },
      updatedAt: Timestamp.now()
    });
    
    // STEP 3: Generate sample data (optional)
    let sampleDataCreated = 0;
    if (options.includeSampleData && template.sampleData.available) {
      console.log(`  Generating sample data...`);
      sampleDataCreated = await generateSampleData(orgId, templateId, options.userId);
    }
    
    console.log(`✅ Template applied successfully`);
    console.log(`   Products created: ${productsCreated}`);
    console.log(`   Sample data items: ${sampleDataCreated}`);
    
    return {
      productsCreated,
      sampleDataCreated
    };
    
  } catch (error) {
    console.error('Error applying template:', error);
    throw error;
  }
}

// ============================================================================
// Product Creation
// ============================================================================

/**
 * Create products from template
 */
async function createProductsFromTemplate(
  orgId: string,
  products: any[],
  leadStages: string[],
  opportunityStages: string[]
): Promise<number> {
  try {
    const batch = writeBatch(db);
    const productsRef = collection(db, 'products');
    
    for (const product of products) {
      const productData: Partial<Product> = {
        key: product.key,
        name: product.name,
        type: product.type,
        orgId,
        isDefault: false,
        leadStages,
        opportunityStages,
        pricing: product.pricing,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      const docRef = doc(productsRef);
      batch.set(docRef, productData);
    }
    
    await batch.commit();
    console.log(`  ✅ Created ${products.length} products`);
    
    return products.length;
  } catch (error) {
    console.error('Error creating products:', error);
    throw error;
  }
}

// ============================================================================
// Sample Data Generation
// ============================================================================

/**
 * Generate sample data based on template
 */
async function generateSampleData(
  orgId: string,
  templateId: string,
  userId: string
): Promise<number> {
  try {
    let totalCreated = 0;
    
    switch (templateId) {
      case 'automotive':
        totalCreated = await generateAutomotiveSampleData(orgId, userId);
        break;
      case 'insurance':
        totalCreated = await generateInsuranceSampleData(orgId, userId);
        break;
      case 'saas':
        totalCreated = await generateSaaSSampleData(orgId, userId);
        break;
      case 'consulting':
        totalCreated = await generateConsultingSampleData(orgId, userId);
        break;
      case 'ecommerce':
        totalCreated = await generateECommerceSampleData(orgId, userId);
        break;
      case 'agency':
        totalCreated = await generateAgencySampleData(orgId, userId);
        break;
      case 'finance':
        totalCreated = await generateFinanceSampleData(orgId, userId);
        break;
      default:
        console.log('  No sample data generator for this template');
    }
    
    return totalCreated;
  } catch (error) {
    console.error('Error generating sample data:', error);
    // Don't throw - sample data is optional
    return 0;
  }
}

/**
 * Generate sample data for Automotive template
 */
async function generateAutomotiveSampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample companies
  const companies = [
    { name: 'Familie Schmidt', email: 'schmidt@example.com', type: 'Privatkunde' },
    { name: 'Müller Logistik GmbH', email: 'mueller@example.com', type: 'Geschäftskunde' },
    { name: 'Familie Weber', email: 'weber@example.com', type: 'Privatkunde' }
  ];
  
  for (const company of companies) {
    const docRef = doc(collection(db, 'companies'));
    batch.set(docRef, {
      ...company,
      orgId,
      industry: 'Automotive Customer',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  // Sample leads (test drive requests)
  const leads = [
    { firstName: 'Max', lastName: 'Mustermann', email: 'max@example.com', stage: 'Probefahrt-Anfrage', product: 'Neuwagen - SUV' },
    { firstName: 'Anna', lastName: 'Schmidt', email: 'anna@example.com', stage: 'Showroom-Besuch', product: 'Gebrauchtwagen - Kompakt' },
    { firstName: 'Thomas', lastName: 'Meyer', email: 'thomas@example.com', stage: 'Probefahrt-Anfrage', product: 'Neuwagen - Elektro' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Website',
      score: 70,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

/**
 * Generate sample data for Insurance template
 */
async function generateInsuranceSampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample leads (quote requests)
  const leads = [
    { firstName: 'Maria', lastName: 'Fischer', email: 'maria@example.com', stage: 'Angebot-Anfrage', product: 'KFZ-Versicherung' },
    { firstName: 'Peter', lastName: 'Wagner', email: 'peter@example.com', stage: 'Beratung geplant', product: 'Hausrat-Versicherung' },
    { firstName: 'Lisa', lastName: 'Becker', email: 'lisa@example.com', stage: 'Angebot-Anfrage', product: 'Lebensversicherung' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Telefon',
      score: 65,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

/**
 * Generate sample data for SaaS template
 */
async function generateSaaSSampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample companies (trial signups)
  const companies = [
    { name: 'TechStart GmbH', email: 'info@techstart.de', employees: '10-50' },
    { name: 'Digital Solutions AG', email: 'contact@digital.de', employees: '50-200' },
    { name: 'Innovation Labs', email: 'hello@labs.com', employees: '5-10' }
  ];
  
  for (const company of companies) {
    const docRef = doc(collection(db, 'companies'));
    batch.set(docRef, {
      ...company,
      orgId,
      industry: 'Technology',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  // Sample leads (trial users)
  const leads = [
    { firstName: 'Michael', lastName: 'Schmidt', email: 'michael@techstart.de', stage: 'Trial-Anmeldung', product: 'Professional-Plan' },
    { firstName: 'Sarah', lastName: 'Müller', email: 'sarah@digital.de', stage: 'Produkt aktiviert', product: 'Business-Plan' },
    { firstName: 'Jan', lastName: 'Weber', email: 'jan@labs.com', stage: 'Engagierter User', product: 'Starter-Plan' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Trial Signup',
      score: 80,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

/**
 * Generate sample data for Consulting template
 */
async function generateConsultingSampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample companies (potential clients)
  const companies = [
    { name: 'TechCorp AG', email: 'info@techcorp.de', employees: '100-500', industry: 'Technology' },
    { name: 'FinanzBeratung GmbH', email: 'contact@finanz.de', employees: '20-50', industry: 'Finance' },
    { name: 'Retail Solutions', email: 'hello@retail.com', employees: '50-100', industry: 'Retail' }
  ];
  
  for (const company of companies) {
    const docRef = doc(collection(db, 'companies'));
    batch.set(docRef, {
      ...company,
      orgId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  // Sample leads (consulting inquiries)
  const leads = [
    { firstName: 'Dr. Marcus', lastName: 'Schmidt', email: 'schmidt@techcorp.de', stage: 'Erstkontakt', product: 'Strategy Consulting' },
    { firstName: 'Andrea', lastName: 'Müller', email: 'mueller@finanz.de', stage: 'Discovery Call', product: 'Business Consulting' },
    { firstName: 'Thomas', lastName: 'Weber', email: 'weber@retail.com', stage: 'Proposal', product: 'IT Consulting' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Referral',
      score: 75,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

/**
 * Generate sample data for E-Commerce template
 */
async function generateECommerceSampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample customers (with purchase history)
  const customers = [
    { name: 'Anna Schmidt', email: 'anna.schmidt@example.com', type: 'VIP-Kunde', totalOrders: 15 },
    { name: 'Max Müller', email: 'max.mueller@example.com', type: 'Standard-Kunde', totalOrders: 3 },
    { name: 'Lisa Weber', email: 'lisa.weber@example.com', type: 'Neukunde', totalOrders: 1 }
  ];
  
  for (const customer of customers) {
    const docRef = doc(collection(db, 'companies'));
    batch.set(docRef, {
      name: customer.name,
      email: customer.email,
      orgId,
      industry: 'E-Commerce Customer',
      description: `${customer.type} mit ${customer.totalOrders} Bestellungen`,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  // Sample leads (customer inquiries)
  const leads = [
    { firstName: 'Julia', lastName: 'Becker', email: 'julia@example.com', stage: 'Anfrage gestellt', product: 'Produkt-Anfrage' },
    { firstName: 'Tom', lastName: 'Fischer', email: 'tom@example.com', stage: 'Kontaktiert', product: 'Online-Bestellung' },
    { firstName: 'Sarah', lastName: 'Wagner', email: 'sarah@example.com', stage: 'Website-Besucher', product: 'Produkt-Anfrage' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Website',
      score: 60,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

/**
 * Generate sample data for Agency template
 */
async function generateAgencySampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample clients (companies that hire the agency)
  const clients = [
    { name: 'StartUp Tech GmbH', email: 'info@startup.de', employees: '10-20', industry: 'Technology' },
    { name: 'Fashion Boutique', email: 'contact@fashion.com', employees: '5-10', industry: 'Retail' },
    { name: 'Restaurant Kette', email: 'marketing@restaurant.de', employees: '50-100', industry: 'Hospitality' }
  ];
  
  for (const client of clients) {
    const docRef = doc(collection(db, 'companies'));
    batch.set(docRef, {
      ...client,
      orgId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  // Sample leads (agency inquiries)
  const leads = [
    { firstName: 'Sophie', lastName: 'Klein', email: 'klein@startup.de', stage: 'Briefing geplant', product: 'Social Media Marketing' },
    { firstName: 'Mark', lastName: 'Braun', email: 'braun@fashion.com', stage: 'Pitch', product: 'Google Ads Management' },
    { firstName: 'Nina', lastName: 'Wolf', email: 'wolf@restaurant.de', stage: 'Erstkontakt', product: 'SEO Services' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Website',
      score: 70,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

/**
 * Generate sample data for Finance template
 */
async function generateFinanceSampleData(orgId: string, userId: string): Promise<number> {
  const batch = writeBatch(db);
  let count = 0;
  
  // Sample customers (bank customers)
  const customers = [
    { name: 'Familie Schneider', email: 'schneider@example.com', type: 'Premium-Kunde', products: 'Girokonto, Baufinanzierung' },
    { name: 'Michael Lang', email: 'lang@example.com', type: 'Standard-Kunde', products: 'Girokonto, Sparkonto' },
    { name: 'Dr. Weber', email: 'weber@example.com', type: 'Investment-Kunde', products: 'Depot, Vermögensberatung' }
  ];
  
  for (const customer of customers) {
    const docRef = doc(collection(db, 'companies'));
    batch.set(docRef, {
      name: customer.name,
      email: customer.email,
      orgId,
      industry: 'Bank Customer',
      description: `${customer.type} mit ${customer.products}`,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  // Sample leads (consultation requests)
  const leads = [
    { firstName: 'Anna', lastName: 'Richter', email: 'richter@example.com', stage: 'Termin vereinbart', product: 'Baufinanzierung' },
    { firstName: 'Tom', lastName: 'Berger', email: 'berger@example.com', stage: 'Beratung durchgeführt', product: 'Investment-Beratung' },
    { firstName: 'Lisa', lastName: 'Hoffmann', email: 'hoffmann@example.com', stage: 'Online-Anfrage', product: 'Privatkredit' }
  ];
  
  for (const lead of leads) {
    const docRef = doc(collection(db, 'leads'));
    batch.set(docRef, {
      ...lead,
      orgId,
      assignedTo: userId,
      source: 'Website',
      score: 75,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    count++;
  }
  
  await batch.commit();
  return count;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get template preview information
 */
export function getTemplateInfo(templateId: string): {
  name: string;
  description: string;
  productsCount: number;
  hasSampleData: boolean;
} | null {
  const template = getTemplateById(templateId);
  if (!template) return null;
  
  return {
    name: template.name,
    description: template.description,
    productsCount: template.defaultProducts.length,
    hasSampleData: template.sampleData.available
  };
}

// ============================================================================
// Export
// ============================================================================

export const onboardingTemplateService = {
  applyTemplateToOrganization,
  getTemplateInfo
};

